import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { useLazyDiagnosticUserIndexQuery } from "~/store/api";
import {
  setHasLoadedDiagnosticInitial,
  setCurrentDiagnosticUser,
  setPotentiallyDiagnosticAuthenticated,
} from "~/store/authentication";
import { useAppDispatch, useAppSelector } from "~/store/hooks";

interface UseLoadCurrentDiagnosticUser {
  hasLoadedDiagnosticInitial: boolean;
}

export const useLoadCurrentDiagnosticUser =
  (): UseLoadCurrentDiagnosticUser => {
    const hasLoadedDiagnosticInitial = useAppSelector(
      (state) => state.authentication.hasLoadedDiagnosticInitial,
    );
    const isPotentiallyDiagnosticAuthenticated = useAppSelector(
      (state) => state.authentication.isPotentiallyDiagnosticAuthenticated,
    );
    const dispatch = useAppDispatch();
    const [apiDiagnosticUserIndex] = useLazyDiagnosticUserIndexQuery();
    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
      // Check for magic link authentication parameter
      const isAuthenticatedParam = searchParams.get("authenticated") === "true";
      if (isAuthenticatedParam && !isPotentiallyDiagnosticAuthenticated) {
        dispatch(setPotentiallyDiagnosticAuthenticated(true));

        // Clean up the URL parameter
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete("authenticated");
        setSearchParams(newSearchParams, { replace: true });
      }

      if (hasLoadedDiagnosticInitial) return;

      // Always attempt to load the diagnostic user via the API.
      // Since DiagnosticUser auth has been consolidated into User auth,
      // users who logged in via server-side Devise (e.g. v2 dashboard)
      // have a valid session but no localStorage flags set.
      // The API uses authenticate_user! so any valid User session works.
      (async () => {
        try {
          const responseBody = await apiDiagnosticUserIndex().unwrap();
          const currentDiagnosticUser = responseBody.data.attributes;
          dispatch(setCurrentDiagnosticUser(currentDiagnosticUser));
        } catch (err: any) {
          // On 401, just mark as loaded — the Protector component will
          // handle redirecting to sign-in if the route requires auth.
          // Don't use signOutDiagnosticUser() here as it does a hard
          // redirect which is unnecessary when Protector handles it.
          if (err?.status !== 401) {
            Sentry.captureException(err);
          }
        } finally {
          dispatch(setHasLoadedDiagnosticInitial(true));
        }
      })();
    }, [
      apiDiagnosticUserIndex,
      dispatch,
      hasLoadedDiagnosticInitial,
      isPotentiallyDiagnosticAuthenticated,
      searchParams,
      setSearchParams,
    ]);

    return { hasLoadedDiagnosticInitial };
  };
